import { useMemo, useState } from 'react'
import { RootNodesQuery } from '../../../apollo/configurator/queries/RootNodes.generated'
import {
    Order,
    OrderBy,
    compareValues,
    getSortValue,
    matchesSearchFilter,
    matchesStatusFilter
} from './modelUtils'

type ModelNode = RootNodesQuery['configuratorNodes'][0]

interface UseModelFilteringProps {
    models: ModelNode[]
}

interface UseModelFilteringReturn {
    filteredAndSortedModels: ModelNode[]
    paginatedModels: ModelNode[]
    searchFilter: string
    setSearchFilter: (value: string) => void
    statusFilter: string
    setStatusFilter: (value: string) => void
    order: Order
    setOrder: (value: Order) => void
    orderBy: OrderBy
    setOrderBy: (value: OrderBy) => void
    page: number
    setPage: (value: number) => void
    rowsPerPage: number
    setRowsPerPage: (value: number) => void
    handleRequestSort: (property: OrderBy) => void
    handleChangePage: (event: unknown, newPage: number) => void
    handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void
}

export const useModelFiltering = ({ models }: UseModelFilteringProps): UseModelFilteringReturn => {
    const [searchFilter, setSearchFilter] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('')
    const [order, setOrder] = useState<Order>('asc')
    const [orderBy, setOrderBy] = useState<OrderBy>('code')
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)

    const filteredAndSortedModels = useMemo(() => {
        let filtered = models.filter((model) => {
            const matchesSearch = matchesSearchFilter(model, searchFilter)
            const matchesStatus = matchesStatusFilter(model, statusFilter)
            return matchesSearch && matchesStatus
        })
        filtered.sort((a, b) => {
            const aValue = getSortValue(a, orderBy)
            const bValue = getSortValue(b, orderBy)
            return compareValues(aValue, bValue, order)
        })

        return filtered
    }, [models, searchFilter, statusFilter, order, orderBy])

    const paginatedModels = useMemo(() => {
        const start = page * rowsPerPage
        return filteredAndSortedModels.slice(start, start + rowsPerPage)
    }, [filteredAndSortedModels, page, rowsPerPage])

    const handleRequestSort = (property: OrderBy) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    return {
        filteredAndSortedModels,
        paginatedModels,
        searchFilter,
        setSearchFilter,
        statusFilter,
        setStatusFilter,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        handleRequestSort,
        handleChangePage,
        handleChangeRowsPerPage
    }
}
